import { useState } from 'react';
import { useRevalidator } from 'react-router';

import { Button, Card } from '@ds/core';
import { Alert } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { SegmentedControl } from '@ds/navigation';
import { completarEndereco, consultarCep, digitosDoCep } from '@/comuns/cep';
import { useAviso } from '@/comuns/toast';
import { faltasDaClinica } from '@/data/dadosDaClinica';
import type { FaltasDaClinica } from '@/data/dadosDaClinica';
import { dados } from '@/data/porta';
import { LogotipoDaClinica } from './LogotipoDaClinica';
import type { CargaDeConfiguracoes, DadosDaClinica, UnidadeDaClinica } from '@/data/tipos';
import { pode } from '@/session/sessao';
import { UnidadesDaClinica } from './UnidadesDaClinica';

const FUSOS = [
    { value: 'America/Sao_Paulo', label: '(GMT-03:00) São Paulo' },
    { value: 'America/Manaus', label: '(GMT-04:00) Manaus' },
    { value: 'America/Rio_Branco', label: '(GMT-05:00) Rio Branco' },
    { value: 'America/Noronha', label: '(GMT-02:00) Fernando de Noronha' },
];

/**
 * Dados da clínica e da unidade.
 *
 * O endereço é da UNIDADE, não da clínica: rede com oito unidades tem um
 * cadastro e oito endereços, e obrigar a escolher "o" endereço da rede seria
 * inventar um dado que a clínica não tem.
 */
export function DadosDaClinicaAba({ carga }: { carga: CargaDeConfiguracoes }) {
    const revalidator = useRevalidator();
    const aviso = useAviso();

    const [clinica, setClinica] = useState<DadosDaClinica>(carga.clinica);
    /* Qual unidade o cartão de endereço edita. Arquivada, ou ainda não relida depois de
       criada, cai na primeira; e o rascunho de uma unidade não vale para outra. */
    const [escolhidaId, setEscolhidaId] = useState<string | undefined>(carga.unidades[0]?.id);
    const original = carga.unidades.find((u) => u.id === escolhidaId) ?? carga.unidades[0];
    const [rascunho, setUnidade] = useState<UnidadeDaClinica | undefined>(original);
    const unidade = rascunho && rascunho.id === original?.id ? rascunho : original;
    const [salvando, setSalvando] = useState(false);

    const sujo = JSON.stringify(clinica) !== JSON.stringify(carga.clinica)
        || JSON.stringify(unidade) !== JSON.stringify(original);

    const campo = <T,>(atual: T, mudar: (v: T) => void) => (chave: keyof T) => (valor: string) =>
        mudar({ ...atual, [chave]: valor });

    const [faltas, setFaltas] = useState<FaltasDaClinica>({});
    const naClinica = campo(clinica, (v: DadosDaClinica) => { setFaltas({}); setClinica(v); });
    const naUnidade = unidade ? campo(unidade, (v: UnidadeDaClinica) => { setFaltas({}); setUnidade(v); }) : () => () => { };

    const salvar = async () => {
        /* Os campos marcados como obrigatórios eram gravados vazios. */
        const encontradas = faltasDaClinica(clinica, unidade);
        if (Object.keys(encontradas).length > 0) {
            setFaltas(encontradas);
            return;
        }
        setSalvando(true);
        try {
            /* Clínica e unidade numa chamada só: em duas, a segunda falhava com
               a primeira já gravada e o aviso dizia que nada tinha sido salvo. */
            await dados().salvarDadosDaClinicaEUnidade(
                {
                    nome: clinica.nome,
                    razaoSocial: clinica.razaoSocial,
                    documento: clinica.documento,
                    tipoPessoa: clinica.tipoPessoa,
                    email: clinica.email,
                    fuso: clinica.fuso,
                },
                unidade
                    ? {
                        id: unidade.id,
                        mudancas: {
                            nome: unidade.nome, telefone: unidade.telefone, email: unidade.email,
                            cep: unidade.cep, estado: unidade.estado, cidade: unidade.cidade,
                            bairro: unidade.bairro, rua: unidade.rua, numero: unidade.numero,
                            complemento: unidade.complemento,
                        },
                    }
                    : undefined,
            );
            aviso({ title: 'Dados salvos', description: clinica.nome });
            revalidator.revalidate();
        } catch (e) {
            aviso({
                title: 'Não foi possível salvar',
                description: e instanceof Error ? e.message : 'Tente de novo.',
                tone: 'danger',
            });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Card
                title="Dados da clínica"
                subtitle="O nome fantasia aparece na tela; a razão social é o que sai em documento."
                padding={20}
                actions={(
                    <span style={{ display: 'flex', gap: 8 }}>
                        <Button
                            variant="ghost" disabled={!sujo || salvando}
                            onClick={() => { setClinica(carga.clinica); setUnidade(original); }}
                        >
                            Descartar
                        </Button>
                        <Button onClick={salvar} disabled={!sujo || salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </span>
                )}
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(240px,100%),1fr))', gap: 14 }}>
                    <FormField label="Tipo de pessoa" required>
                        <SegmentedControl
                            ariaLabel="Tipo de pessoa" value={clinica.tipoPessoa}
                            onChange={(v) => setClinica({ ...clinica, tipoPessoa: v === 'fisica' ? 'fisica' : 'juridica' })}
                            options={[{ value: 'fisica', label: 'Física' }, { value: 'juridica', label: 'Jurídica' }]}
                        />
                    </FormField>
                    <FormField label={clinica.tipoPessoa === 'fisica' ? 'CPF' : 'CNPJ'} htmlFor="cfg-documento" required error={faltas.documento}>
                        <Input id="cfg-documento" value={clinica.documento} onChange={naClinica('documento')} />
                    </FormField>
                    <FormField label="Nome fantasia" htmlFor="cfg-nome" required error={faltas.nome}>
                        <Input id="cfg-nome" value={clinica.nome} onChange={naClinica('nome')} />
                    </FormField>
                    <FormField label="Razão social" htmlFor="cfg-razao">
                        <Input id="cfg-razao" value={clinica.razaoSocial} onChange={naClinica('razaoSocial')} />
                    </FormField>
                    <FormField label="E-mail comercial" htmlFor="cfg-email">
                        <Input id="cfg-email" type="email" value={clinica.email} onChange={naClinica('email')} />
                    </FormField>
                    <FormField
                        label="Fuso horário" htmlFor="cfg-fuso"
                        hint="Base de data e hora de agendamentos, títulos e relatórios."
                    >
                        <Select id="cfg-fuso" value={clinica.fuso} onChange={naClinica('fuso')} options={FUSOS} />
                    </FormField>
                </div>
            </Card>

            <UnidadesDaClinica
                unidades={carga.unidades} escolhidaId={unidade?.id} podeGerir={pode('configuracoes')}
                onEscolher={(id) => {
                    if (id === unidade?.id) return;
                    /* Trocar com alteração pendente jogaria fora o que foi digitado. */
                    if (sujo) {
                        aviso({ title: 'Salve ou descarte antes de trocar de unidade', tone: 'warning' });
                        return;
                    }
                    setEscolhidaId(id);
                }}
                onMudou={() => revalidator.revalidate()}
            />

            {unidade ? (
                <Card
                    title={`Endereço e contato — ${unidade.nome}`}
                    subtitle="Sai nas notificações ao paciente e no cabeçalho dos documentos impressos."
                    padding={20}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(220px,100%),1fr))', gap: 14 }}>
                        <FormField label="Telefone" htmlFor="cfg-telefone" required error={faltas.telefone}>
                            <Input id="cfg-telefone" value={unidade.telefone} onChange={naUnidade('telefone')} />
                        </FormField>
                        <FormField label="E-mail" htmlFor="cfg-unidade-email">
                            <Input id="cfg-unidade-email" type="email" value={unidade.email} onChange={naUnidade('email')} />
                        </FormField>
                        <FormField label="CEP" htmlFor="cfg-cep" hint="Ao sair do campo, o endereço vazio é completado pelo CEP.">
                            <Input
                                id="cfg-cep" value={unidade.cep} onChange={naUnidade('cep')}
                                onBlur={() => {
                                    const cepDigitado = unidade.cep;
                                    if (!digitosDoCep(cepDigitado)) return;
                                    void consultarCep(cepDigitado).then((r) => {
                                        if (r.ok) setUnidade((u) => (u ? completarEndereco(u, r.endereco) : u));
                                        else aviso({ title: 'Endereço não completado', description: r.motivo });
                                    });
                                }}
                            />
                        </FormField>
                        <FormField label="Estado" htmlFor="cfg-estado">
                            <Input id="cfg-estado" value={unidade.estado} onChange={naUnidade('estado')} />
                        </FormField>
                        <FormField label="Cidade" htmlFor="cfg-cidade">
                            <Input id="cfg-cidade" value={unidade.cidade} onChange={naUnidade('cidade')} />
                        </FormField>
                        <FormField label="Bairro" htmlFor="cfg-bairro">
                            <Input id="cfg-bairro" value={unidade.bairro} onChange={naUnidade('bairro')} />
                        </FormField>
                        <FormField label="Rua" htmlFor="cfg-rua">
                            <Input id="cfg-rua" value={unidade.rua} onChange={naUnidade('rua')} />
                        </FormField>
                        <FormField label="Número" htmlFor="cfg-numero">
                            <Input id="cfg-numero" value={unidade.numero} onChange={naUnidade('numero')} />
                        </FormField>
                        <FormField label="Complemento" htmlFor="cfg-complemento">
                            <Input id="cfg-complemento" value={unidade.complemento} onChange={naUnidade('complemento')} />
                        </FormField>
                    </div>
                </Card>
            ) : (
                <Alert tone="warning" title="Esta clínica não tem unidade cadastrada">
                    O endereço mora na unidade — sem ela não há para onde escrever.
                </Alert>
            )}

            {/* O logotipo tem balde próprio desde a 0098. */}
            <LogotipoDaClinica />
        </div>
    );
}
